import React, { useState } from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  Grid,
  Typography,
  useTheme,
  Skeleton,
  Grid2,
} from "@mui/material";
import { useParams } from "react-router-dom";
import {
  useGetSafetyKpisQuery,
  useGetOpenCloseNcrSummaryQuery,
} from "store/slices/apis/safetyDashboardSlice";
import SafetyPopup from "./SafetyPopup";
import { MdOutlineArrowOutward } from "react-icons/md";
import ProjectStatusOverviewHead from "../../../../components/ProjectStatusOverviewHead";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { ReactComponent as ArrowIcon } from "assets/PwC_Funct_Icons_ArrowIncrease_Outline_Black_RGB.svg"
import { ReactComponent as ArrowUpIcon } from "assets/PwC_Funct_Icons_ArrowUp_Outline_Black_RGB.svg"
import { ReactComponent as ArrowDownIcon } from "assets/PwC_Funct_Icons_ArrowDown_Outline_Black_RGB.svg"

const SafetyKPI = () => {
  const theme = useTheme();
  const { projectId } = useParams();

  const { data: safetyKpiData, isLoading: isKpiLoading } =
    useGetSafetyKpisQuery({ projectId });
  const { data: ncrData, isLoading: isNcrLoading } =
    useGetOpenCloseNcrSummaryQuery({ projectId });

  const isLoading = isKpiLoading || isNcrLoading;

  const [popupData, setPopupData] = useState(null);
  const [open, setOpen] = useState(false);

  const kpi = safetyKpiData?.data || {};

  const lastUpdated = safetyKpiData?.data?.lastUpdated;

  const kpiItems = [
    {
      title: "Total Man-hours",
      value: kpi?.totalManHours?.toLocaleString(),
      change: `${kpi?.totalManHours?.growthPercentage ?? 0}%`,
      status: "Safety-kpi",
      highlight: false,
      isPositive: isLoading
        ? true
        : parseFloat(kpi?.totalManHours?.growthPercentage ?? 0) > 0
          ? true
          : false,
      arrow: `${parseFloat(kpi?.totalManHours?.growthPercentage ?? 0) >= 0
          ? "up"
          : "down"
        }`,
      absoluteChange: isNaN(kpi?.totalManHours?.growthPercentage)
        ? "0%"
        : kpi?.totalManHours?.growthPercentage === -100
          ? "0%"
          : `${Math.abs(kpi?.totalManHours?.growthPercentage ?? 0)}%`,
      textColor: "#377E36",
      dotColor: "#CE412D",
    },
    {
      title: "Total Near miss incidents",
      value: kpi?.totalNearMissIncidents?.value,
      change: `${kpi?.totalNearMissIncidents?.growthPercentage ?? 0}%`,
      status: "Safety-kpi",
      highlight: false,
      isPositive: isLoading
        ? true
        : parseFloat(kpi?.totalNearMissIncidents?.growthPercentage ?? 0) > 0
          ? false
          : true,
      arrow: `${parseFloat(kpi?.totalNearMissIncidents?.growthPercentage ?? 0) >= 0
          ? "up"
          : "down"
        }`,
      absoluteChange: isNaN(kpi?.totalNearMissIncidents?.growthPercentage)
        ? "-"
        : kpi?.totalNearMissIncidents?.growthPercentage === -100
          ? "0%"
          : `${Math.abs(kpi?.totalNearMissIncidents?.growthPercentage)}%`,
      textColor: "#377E36",
      dotColor: "#CE412D",
    },
    {
      title: "Total Lost time Incidents ",
      value: kpi?.totalLostTimeIncidents?.value,
      change: `${kpi?.totalLostTimeIncidents?.growthPercentage ?? 0}%`,
      status: "Safety-kpi",
      highlight: false,
      isPositive: isLoading
        ? true
        : parseFloat(kpi?.totalLostTimeIncidents?.growthPercentage ?? 0) > 0
          ? false
          : true,
      arrow: `${parseFloat(kpi?.totalLostTimeIncidents?.growthPercentage ?? 0) >= 0
          ? "up"
          : "down"
        }`,
      absoluteChange: isNaN(kpi?.totalLostTimeIncidents?.growthPercentage)
        ? "-"
        : kpi?.totalLostTimeIncidents?.growthPercentage === -100
          ? "0%"
          : `${Math.abs(kpi?.totalLostTimeIncidents?.growthPercentage)}%`,
      textColor: "#377E36",
      dotColor: "#CE412D",
    },
    {
      title: "Total Fatalities",
      value: kpi?.totalFatalities?.value,
      change: `${kpi?.totalFatalities?.growthPercentage ?? 0}%`,
      status: "Safety-kpi",
      highlight: true,
      isPositive: isLoading
        ? true
        : parseFloat(kpi?.totalFatalities?.growthPercentage ?? 0) > 0
          ? false
          : true,
      arrow: `${parseFloat(kpi?.totalFatalities?.growthPercentage ?? 0) >= 0
          ? "up"
          : "down"
        }`,
      absoluteChange: isNaN(kpi?.totalFatalities?.growthPercentage)
        ? "-"
        : kpi?.totalFatalities?.growthPercentage === -100
          ? "0%"
          : `${Math.abs(kpi?.totalFatalities?.growthPercentage)}%`,
      textColor: "#377E36",
      dotColor: "#CE412D",
    },
  ];

  return (
    <Box>
      <Grid2 container spacing={5} sx={{ mt: "20px" }}>
        <ProjectStatusOverviewHead
          lastModified={lastUpdated}
          marginBottm={"30px"}
        />
      </Grid2>

      <Grid container spacing={2}>
        {(isLoading ? Array(4).fill({}) : kpiItems).map((card, index) => (
          <Grid item xs={12} md={3} key={index}>
            <Card
              sx={{
                boxShadow: "none",
                border: "1px solid #25252517",
                borderRadius: "3px",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                p={2}
              >
                <Box display="flex" alignItems="center">
                  {isLoading ? (
                    <Skeleton
                      variant="rectangular"
                      width={40}
                      height={40}
                      sx={{ mr: 2 }}
                    />
                  ) : (
                    <Box
                      sx={{ bgcolor: "#9ED3CC", width: 40, height: 40, mr: 2 }}
                    />
                  )}

                  <Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      {isLoading ? (
                        <>
                          <Skeleton variant="text" width={60} height={24} />
                          <Skeleton variant="text" width={50} height={20} />
                        </>
                      ) : (
                        <>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: card.highlight ? "#CB402D" : "#252525",
                              fontSize: "18px",
                            }}
                          >
                            {card.value ?? "-"}
                          </Typography>
                          <Chip
                            label={card.absoluteChange}
                            size="small"
                            sx={{
                              backgroundColor: "#E6F4EA",
                              color:
                                card.isPositive === false
                                  ? card.absoluteChange === "0%"
                                    ? card.textColor
                                    : card.dotColor
                                  : card.textColor,
                              fontWeight: 600,
                              fontSize: "12px",
                            }}
                            icon={
                              card.absoluteChange !== "0%" &&
                              // (card.arrow === "up" ? (
                              //   <ArrowUpward
                              //     sx={{
                              //       fontSize: "12px !important",
                              //       color: card.isPositive
                              //         ? `${card.textColor} !important`
                              //         : `${card.dotColor} !important`,
                              //     }}
                              //   />
                              // ) : (
                              //   <ArrowDownward
                              //     sx={{
                              //       fontSize: "12px !important",
                              //       color: card.isPositive
                              //         ? `${card.textColor} !important`
                              //         : `${card.dotColor} !important`,
                              //     }}
                              //   />
                              // ))
                              (card.arrow === "up" ? (
                                <ArrowUpIcon
                                  style={{
                                    height: "12px",
                                    width: "12px",
                                    fill: card.isPositive ? card.textColor : card.dotColor,
                                  }}
                                />
                              ) : (
                                <ArrowDownIcon
                                  style={{
                                    height: "12px",
                                    width: "12px",
                                    fill: card.isPositive ? card.textColor : card.dotColor,
                                  }}
                                />
                              ))

                            }
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              ml: "2px",
                              fontWeight: 400,
                              fontSize: "13px",
                              lineHeight: "24px",
                              letterSpacing: "0.4px",
                              color: "#474747",
                            }}
                          >
                            this month
                          </Typography>
                        </>
                      )}
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{ color: "#252525", fontWeight: 500, mt: 0.5 }}
                    >
                      {isLoading ? (
                        <Skeleton width={100} height={20} />
                      ) : (
                        card.title
                      )}
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  {isLoading ? (
                    <Skeleton variant="circular" width={37} height={37} />
                  ) : (
                    <Avatar
                      sx={{
                        bgcolor: "#fff",
                        width: 37,
                        height: 37,
                        border: "1px solid #d3d3d3",
                      }}
                    >
                      {/* <MdOutlineArrowOutward
                        style={{
                          color: "#252525",
                          fontSize: "20px",
                          cursor: "pointer",
                         
                        }}
                        onClick={() => {
                          setPopupData(card);
                          setOpen(true);
                        }}
                      /> */}
                      <ArrowIcon
                        style={{
                          color: "#252525",
                          fontSize: "20px",
                          cursor: "pointer",
                          height: "13px",
                          stroke: "#252525",
                          strokeWidth: "6"
                        }}
                        onClick={() => {
                          setPopupData(card);
                          setOpen(true);
                        }}
                      />
                    </Avatar>
                  )}
                </Box>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {open && (
        <SafetyPopup
          title={popupData?.title}
          value={popupData?.value}
          status={popupData?.status}
          onOpen={open}
          onClose={() => setOpen(false)}
        />
      )}
    </Box>
  );
};

export default SafetyKPI;
